import React, { useEffect, useState } from "react";
import { Table, Tag, Button, Space, Popconfirm, message, Typography } from "antd";
import { EditOutlined, SendOutlined, RedoOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { fetchShippingList, sendShippingEvent } from "./shippingThunk";
import ShippingEditModal from "./components/ShippingEditModal";

const { Title } = Typography;

const STATUS_COLORS = {
    not_started: "default",
    pending: "blue",
    sent: "green",
    failed: "red",
};

const TYPE_LABELS = {
    sales: "Sales",
    purchase_return: "Purchase Return",
};

const ShippingRecord = () => {
    const dispatch = useDispatch();
    const { list, listLoading } = useSelector((s) => s.tatmeenShipping);

    const [sendingId, setSendingId] = useState(null);
    const [editingRecord, setEditingRecord] = useState(null);

    const loadList = () => dispatch(fetchShippingList());

    useEffect(() => {
        loadList();
    }, [dispatch]);

    const handleSend = async (id) => {
        setSendingId(id);
        try {
            const result = await dispatch(sendShippingEvent(id)).unwrap();
            message.success(result.message || "Shipping event sent successfully");
            loadList();
        } catch (err) {
            message.error(err || "Failed to send shipping event");
            loadList();
        } finally {
            setSendingId(null);
        }
    };

    const columns = [
        {
            title: "Type",
            dataIndex: "shipping_type",
            key: "shipping_type",
            render: (type) => TYPE_LABELS[type] || type,
        },
        { title: "SSCC Code", dataIndex: "sscc_code", key: "sscc_code" },
        { title: "Sender GLN", dataIndex: "sender_gln", key: "sender_gln" },
        { title: "Destination GLN", dataIndex: "receiver_gln", key: "receiver_gln" },
        {
            title: "Status",
            dataIndex: "tatmeen_sent_status",
            key: "tatmeen_sent_status",
            render: (status) => (
                <Tag color={STATUS_COLORS[status] || "default"}>
                    {(status || "not_started").replace("_", " ").toUpperCase()}
                </Tag>
            ),
        },
        { title: "Tatmeen Message ID", dataIndex: "tatmeen_message_id", key: "tatmeen_message_id" },
        // {
        //     title: "Created At",
        //     dataIndex: "created_at",
        //     key: "created_at",
        //     render: (value) => (value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-"),
        // },
        {
            title: "Action",
            key: "action",
            render: (_, record) => {
                const isSent = record.tatmeen_sent_status === "sent";
                const isFailed = record.tatmeen_sent_status === "failed";
                return (
                    <Space>
                        <Button
                            icon={<EditOutlined />}
                            disabled={isSent}
                            onClick={() => setEditingRecord(record)}
                        >
                            Edit
                        </Button>
                        <Popconfirm
                            title={isFailed ? "Retry sending this shipping event?" : "Send this shipping event to Tatmeen?"}
                            description="This sends a live EPCIS shipping event and cannot be undone."
                            onConfirm={() => handleSend(record.id)}
                            disabled={isSent}
                        >
                            <Button
                                type="primary"
                                danger={isFailed}
                                icon={isFailed ? <RedoOutlined /> : <SendOutlined />}
                                disabled={isSent}
                                loading={sendingId === record.id}
                            >
                                {isFailed ? "Retry" : "Send to Tatmeen"}
                            </Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <div style={{ padding: 20 }}>
            <Title level={3} style={{ margin: 0, marginBottom: 16 }}>
                Shipping Record
            </Title>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={list}
                loading={listLoading}
                pagination={{ pageSize: 10 }}
                scroll={{ x: true }}
            />

            <ShippingEditModal
                open={!!editingRecord}
                record={editingRecord}
                onClose={() => setEditingRecord(null)}
                onUpdated={loadList}
            />
        </div>
    );
};

export default ShippingRecord;
